/**
 * Patriotic backdrop: looking out over city rooftops (water towers, antennas,
 * lit windows) toward a detailed Statue of Liberty silhouette rising in the
 * center distance, under a starry night sky with a soft glow behind her so the
 * silhouette reads. Behind content, `pointer-events: none`. Scene colors come
 * from theme vars (`--statue`, `--flame`, `--bldg-*`, `--window`, `--lib-glow`);
 * the sky gradient itself is on the theme `body`. The fireworks are a separate
 * ambient effect.
 */
import React from 'react';

import { Z } from '@shared/constants/zIndex';

const STARS: Array<[string, string]> = [
  ['16%', '10%'], ['64%', '18%'], ['82%', '8%'], ['38%', '24%'],
  ['90%', '14%'], ['8%', '30%'], ['46%', '6%'], ['74%', '12%'], ['28%', '20%'],
];

/** Fixed Statue-of-Liberty-over-the-rooftops backdrop. */
export const PatrioticBackdrop: React.FC = () => (
  <div
    aria-hidden
    data-testid="patriotic-backdrop"
    className="pointer-events-none fixed inset-0"
    style={{ zIndex: Z.BACKDROP }}
  >
    {/* stars */}
    {STARS.map(([left, top]) => (
      <span
        key={`${left}-${top}`}
        className="absolute rounded-full"
        style={{ left, top, width: 2, height: 2, background: '#fff', opacity: 0.55 }}
      />
    ))}

    {/* soft glow behind the statue */}
    <div
      className="absolute"
      style={{
        top: '14%', left: '50%', transform: 'translateX(-50%)',
        width: '30%', height: '60%', borderRadius: '50%',
        background: 'radial-gradient(circle, var(--lib-glow), transparent 70%)', opacity: 0.4,
      }}
    />

    {/* distant haze buildings */}
    <svg className="absolute bottom-0 left-0 w-full" style={{ height: '46%' }} viewBox="0 0 400 90" preserveAspectRatio="none">
      <g fill="var(--bldg-far)">
        <rect x="10" y="40" width="34" height="50" /><rect x="50" y="30" width="26" height="60" /><rect x="84" y="48" width="30" height="42" /><rect x="120" y="36" width="22" height="54" /><rect x="150" y="44" width="30" height="46" /><rect x="220" y="42" width="26" height="48" /><rect x="252" y="32" width="30" height="58" /><rect x="290" y="46" width="24" height="44" /><rect x="320" y="38" width="28" height="52" /><rect x="354" y="44" width="34" height="46" />
      </g>
    </svg>

    {/* Statue of Liberty, center distance */}
    <svg
      className="absolute left-1/2"
      style={{ bottom: '30%', width: '14%', height: '52%', transform: 'translateX(-50%)' }}
      viewBox="0 0 100 200"
      preserveAspectRatio="xMidYMax meet"
    >
      <g fill="var(--statue)">
        <rect x="30" y="176" width="40" height="10" /><rect x="23" y="184" width="54" height="8" /><rect x="16" y="190" width="68" height="10" />
        <path d="M40,106 C35,130 32,152 30,176 L70,176 C68,152 65,130 60,106 Q50,114 40,106 Z" />
        <path d="M40,106 L46,150 L52,150 L56,108 Q48,114 40,106 Z" opacity="0.8" />
        <path d="M41,108 Q50,100 59,108 L57,118 Q50,112 43,118 Z" />
        <ellipse cx="50" cy="96" rx="7.5" ry="9" />
        <g>
          <path d="M46,86 L54,86 L50,66 Z" /><path d="M46,86 L54,86 L50,66 Z" transform="rotate(26 50 90)" /><path d="M46,86 L54,86 L50,66 Z" transform="rotate(52 50 90)" /><path d="M46,86 L54,86 L50,66 Z" transform="rotate(-26 50 90)" /><path d="M46,86 L54,86 L50,66 Z" transform="rotate(-52 50 90)" /><path d="M46,86 L54,86 L50,66 Z" transform="rotate(78 50 90)" /><path d="M46,86 L54,86 L50,66 Z" transform="rotate(-78 50 90)" />
        </g>
        <path d="M44,108 Q33,72 28,34" fill="none" stroke="var(--statue)" strokeWidth="8" strokeLinecap="round" />
        <path d="M58,112 Q64,128 64,140" fill="none" stroke="var(--statue)" strokeWidth="7" strokeLinecap="round" />
        <rect x="58" y="120" width="16" height="26" rx="1.5" transform="rotate(16 66 133)" />
        <path d="M23,32 L33,32 L31,24 L25,24 Z" />
      </g>
      <path d="M28,22 C33,15 33,8 28,2 C23,8 23,15 28,22 Z" fill="var(--flame)" />
      <ellipse cx="28" cy="14" rx="2.4" ry="4" fill="oklch(0.92 0.13 92)" />
    </svg>

    {/* midground buildings with lit windows */}
    <svg className="absolute bottom-0 left-0 w-full" style={{ height: '34%' }} viewBox="0 0 400 70" preserveAspectRatio="none">
      <g fill="var(--bldg-mid)">
        <rect x="0" y="34" width="44" height="36" /><rect x="48" y="22" width="34" height="48" /><rect x="120" y="30" width="40" height="40" /><rect x="250" y="28" width="36" height="42" /><rect x="300" y="36" width="44" height="34" /><rect x="350" y="24" width="40" height="46" />
      </g>
      <g fill="var(--window)" opacity="0.85">
        <rect x="10" y="40" width="3" height="4" /><rect x="18" y="40" width="3" height="4" /><rect x="10" y="50" width="3" height="4" /><rect x="26" y="48" width="3" height="4" /><rect x="56" y="30" width="3" height="4" /><rect x="64" y="30" width="3" height="4" /><rect x="56" y="40" width="3" height="4" /><rect x="72" y="38" width="3" height="4" /><rect x="128" y="38" width="3" height="4" /><rect x="138" y="38" width="3" height="4" /><rect x="148" y="46" width="3" height="4" /><rect x="258" y="36" width="3" height="4" /><rect x="268" y="44" width="3" height="4" /><rect x="278" y="36" width="3" height="4" /><rect x="358" y="32" width="3" height="4" /><rect x="368" y="42" width="3" height="4" /><rect x="378" y="32" width="3" height="4" />
      </g>
    </svg>

    {/* foreground rooftops we look over (closest, darkest) */}
    <svg className="absolute bottom-0 left-0 w-full" style={{ height: '30%' }} viewBox="0 0 400 70" preserveAspectRatio="none">
      <defs>
        <symbol id="pat-watertower" viewBox="0 0 40 50">
          <g fill="var(--bldg-near)"><path d="M8,50 L11,28 H29 L32,50 Z" /><rect x="9" y="20" width="22" height="10" /><path d="M7,20 L33,20 L20,8 Z" /></g>
        </symbol>
      </defs>
      <g fill="var(--bldg-near)">
        <rect x="-2" y="30" width="92" height="40" /><rect x="150" y="36" width="96" height="34" /><rect x="320" y="26" width="84" height="44" />
      </g>
      <g fill="var(--bldg-near)" opacity="0.7"><rect x="-2" y="28" width="92" height="4" /><rect x="150" y="34" width="96" height="4" /><rect x="320" y="24" width="84" height="4" /></g>
      <use href="#pat-watertower" x="18" y="4" width="34" height="42" />
      <use href="#pat-watertower" x="330" y="0" width="34" height="42" />
      <g stroke="var(--bldg-near)" strokeWidth="2" strokeLinecap="round">
        <path d="M72,30 V8" /><path d="M66,16 H78" /><path d="M200,36 V12" /><path d="M195,18 H205" /><path d="M360,26 V2" />
      </g>
      <circle cx="360" cy="2" r="2" fill="var(--fw-red, oklch(0.6 0.22 25))" />
      <g fill="var(--window)" opacity="0.8"><rect x="40" y="44" width="3" height="4" /><rect x="48" y="52" width="3" height="4" /><rect x="170" y="48" width="3" height="4" /><rect x="180" y="56" width="3" height="4" /><rect x="340" y="40" width="3" height="4" /></g>
    </svg>
  </div>
);
